import React, { useEffect, useState } from 'react';

import { DatevBookingData } from '../types';

import BookingTable from './datevBooking/BookingTable';
import {
  ALL_COLUMNS,
  COLUMN_CONFIGS,
  HIDDEN_COLUMNS,
  ExtendedDatevBookingData,
  buildEmptyEntry,
  initializeUiBookingRows,
} from './datevBooking/constants';
import DatevBookingHeader from './datevBooking/DatevBookingHeader';
import { bookingDataToCsv } from './datevBooking/utils/bookingDataToCsv';
import { downloadCsv } from './datevBooking/utils/downloadCsv';

interface DatevBookingDataDisplayProps {
  bookingData: DatevBookingData[];
  originalBookingData?: DatevBookingData[];
  onSave?: (updatedData: DatevBookingData[]) => void;
  onRestore?: () => void;
  onEditModeChange?: (isEditing: boolean) => void;
  renderExportAction?: (data: DatevBookingData[]) => React.ReactNode;
  showHeader?: boolean;
}

const DatevBookingDataDisplay: React.FC<DatevBookingDataDisplayProps> = ({
  bookingData,
  originalBookingData,
  onSave,
  onRestore,
  onEditModeChange,
  renderExportAction,
  showHeader = true,
}) => {
  const [isEditing, setIsEditingState] = useState(false);

  const setIsEditing = (editing: boolean) => {
    setIsEditingState(editing);
    onEditModeChange?.(editing);
  };
  const [editedData, setEditedData] = useState<ExtendedDatevBookingData[]>(
    initializeUiBookingRows(bookingData)
  );
  const [visibleColumns, setVisibleColumns] = useState(
    new Set(ALL_COLUMNS.filter(col => !HIDDEN_COLUMNS.includes(col)))
  );

  // Update editedData when bookingData changes from parent (only when not editing)
  useEffect(() => {
    if (!isEditing) {
      setEditedData(initializeUiBookingRows(bookingData));
    }
  }, [bookingData, isEditing]);

  const handleSave = () => {
    if (onSave) {
      onSave(editedData);
    }
    setIsEditing(false);
  };

  const updateVisibleColumns = () => {
    if (visibleColumns.size === ALL_COLUMNS.length) {
      setVisibleColumns(
        new Set(ALL_COLUMNS.filter(col => !HIDDEN_COLUMNS.includes(col)))
      );
    } else {
      setVisibleColumns(new Set(ALL_COLUMNS));
    }
  };

  const handleCancel = () => {
    setEditedData(initializeUiBookingRows(bookingData));
    setIsEditing(false);
  };

  const updateEntry = (
    index: number,
    field: keyof DatevBookingData,
    value: string | number | null
  ) => {
    const updatedEntries = [...editedData];
    updatedEntries[index] = { ...updatedEntries[index], [field]: value };
    setEditedData(updatedEntries);
  };

  const addEntry = () => {
    setEditedData([...editedData, buildEmptyEntry()]);
  };

  const removeEntry = (index: number) => {
    const updatedEntries = editedData.filter((_, i) => i !== index);
    setEditedData(updatedEntries);
  };

  const displayData: ExtendedDatevBookingData[] = isEditing
    ? editedData
    : initializeUiBookingRows(bookingData);
  const hasChanges = originalBookingData
    ? JSON.stringify(bookingData) !== JSON.stringify(originalBookingData)
    : false;
  const exportAction = renderExportAction
    ? renderExportAction(displayData)
    : undefined;

  const handleDownloadCsv = () => {
    const csv = bookingDataToCsv(displayData, COLUMN_CONFIGS);
    downloadCsv(csv);
  };

  return (
    <div className='w-full'>
      {showHeader && (
        <DatevBookingHeader
          entryCount={displayData.length}
          isEditing={isEditing}
          hasChanges={hasChanges}
          exportAction={exportAction}
        />
      )}

      <div className='mb-3 flex flex-col gap-2'>
        <div className='flex justify-end gap-6 items-center'>
          {hasChanges && !isEditing && onRestore && (
            <div className='flex items-center gap-2 text-xs italic text-amber-700'>
              <span>
                Booking data has been modified. Changes are temporary and will
                be lost on page reload.
              </span>
              <button
                onClick={onRestore}
                className='text-blue-600 hover:text-blue-800 underline'
              >
                Restore original
              </button>
            </div>
          )}

          <div
            className={hasChanges && !isEditing && onRestore ? '' : 'ml-auto'}
          >
            {!isEditing && onSave && (
              <button
                onClick={() => setIsEditing(true)}
                className='p-2 text-gray-600 hover:text-blue-600 flex items-center gap-1 text-sm'
                title='Edit'
              >
                Edit
              </button>
            )}
          </div>

          {isEditing && (
            <div className='flex gap-2 mt-4'>
              <button
                onClick={handleSave}
                className='text-sm px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1'
              >
                Done
              </button>
              <button
                onClick={handleCancel}
                className='text-sm px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1'
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <BookingTable
        data={displayData}
        columns={COLUMN_CONFIGS}
        visibleColumns={visibleColumns}
        isEditing={isEditing}
        onRemove={removeEntry}
        onChangeCell={updateEntry}
        onAdd={isEditing ? addEntry : undefined}
        onToggleColumns={updateVisibleColumns}
      />

      {!isEditing && (
        <div className='mt-3 text-right'>
          <button
            onClick={handleDownloadCsv}
            className='text-sm text-blue-600 hover:text-blue-800 underline'
          >
            Download as CSV
          </button>
        </div>
      )}
    </div>
  );
};

export default DatevBookingDataDisplay;
